/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { EBillsStatus, IBill } from 'modules/MyBills';
import { BillsTableWrapper } from 'modules/MyBills/components/BillingHistory/styles';
import { Table, HeaderSort } from '@myob/myob-widgets';
import InvoiceStatus from 'modules/MyBills/components/InvoiceStatus/InvoiceStatus';
import { mediaPoints } from 'styles/variables';
import { ActionDropdown } from 'modules/MyBills/components/BillingHistory/BillsTable/ActionDropdown/ActionDropdown';
import { ETableFields, getTableValueFromInvoice } from 'modules/MyBills/components/BillingHistory/helper';
import { TSortState } from 'modules/MyBills/MyBills';

export type Props = {
    bills: IBill[];
    activeSort: TSortState['activeSort'];
    onSort: (column: ETableFields) => void;
};

export function showStatusOrDash(status: EBillsStatus | null) {
    return status ? <InvoiceStatus status={status} /> : '-';
}

export const BillsTable: React.FC<Props> = ({ bills, activeSort, onSort }) => {
    return (
        <BillsTableWrapper>
            <div data-testid="billsTable">
                <Table
                    hasCard
                    hasActions
                    responsiveWidths={[
                        {
                            'max-width': mediaPoints.sm - 1 + 'px',
                            config: [{ columnName: 'Action', styles: { width: '3.5rem' } }],
                        },
                        {
                            'min-width': mediaPoints.sm + 'px',
                            config: [{ columnName: 'Action', styles: { width: '7rem' } }],
                        },
                        {
                            'min-width': mediaPoints.md + 'px',
                            config: [
                                {
                                    columnName: 'Date issued',
                                    styles: { width: '13rem' },
                                },
                                {
                                    columnName: 'Invoice number',
                                    styles: { width: '15rem' },
                                },
                                {
                                    columnName: 'Billing period',
                                    styles: { width: '22rem' },
                                },
                                {
                                    columnName: 'Status',
                                    styles: { width: '10.5rem' },
                                },
                            ],
                        },
                    ]}
                >
                    <Table.Header>
                        <Table.HeaderItem columnName="Date issued">
                            <HeaderSort
                                activeSort={activeSort}
                                sortName={ETableFields.DateIssued}
                                onSort={onSort}
                                title="Date issued"
                            />
                        </Table.HeaderItem>
                        <Table.HeaderItem columnName="Invoice number">Invoice number</Table.HeaderItem>
                        <Table.HeaderItem columnName="Billing period">
                            <HeaderSort
                                activeSort={activeSort}
                                sortName={ETableFields.BillingPeriod}
                                onSort={onSort}
                                title="Billing period"
                            />
                        </Table.HeaderItem>
                        <Table.HeaderItem columnName="Total amount">
                            <HeaderSort
                                activeSort={activeSort}
                                sortName={ETableFields.TotalAmount}
                                onSort={onSort}
                                title="Total amount"
                            />
                        </Table.HeaderItem>
                        <Table.HeaderItem columnName="Status">
                            <HeaderSort
                                activeSort={activeSort}
                                sortName={ETableFields.Status}
                                onSort={onSort}
                                title="Status"
                            />
                        </Table.HeaderItem>
                        <Table.HeaderItem columnName="Action" align="right">
                            Action
                        </Table.HeaderItem>
                    </Table.Header>
                    <Table.Body>
                        {bills.map((bill, index) => (
                            <div key={bill.invoiceNumber} data-testid={`bill-history-${index}`}>
                                <Table.Row>
                                    <Table.RowItem columnName="Date issued">
                                        {getTableValueFromInvoice(bill, ETableFields.DateIssued)}
                                    </Table.RowItem>
                                    <Table.RowItem columnName="Invoice number">
                                        {getTableValueFromInvoice(bill, ETableFields.InvoiceNumber)}
                                    </Table.RowItem>
                                    <Table.RowItem columnName="Billing period">
                                        {getTableValueFromInvoice(bill, ETableFields.BillingPeriod)}
                                    </Table.RowItem>
                                    <Table.RowItem columnName="Total amount">
                                        {getTableValueFromInvoice(bill, ETableFields.TotalAmount)}
                                    </Table.RowItem>
                                    <Table.RowItem columnName="Status">{showStatusOrDash(bill?.status)}</Table.RowItem>
                                    <Table.RowItem columnName="Action" cellRole="actions" align="right" valign="middle">
                                        <ActionDropdown
                                            invoiceNumber={bill.invoiceNumber}
                                            status={bill.status}
                                            downloadId={bill.downloadId}
                                        />
                                    </Table.RowItem>
                                </Table.Row>
                            </div>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </BillsTableWrapper>
    );
};

export default BillsTable;
